import React, { useEffect, useRef, useState } from "react";

import { SbNotification } from "uiCore";
import { PAIC_TAB_KEYS, PAIC_VIEW_MODES, PAIC_PROJECT_SOURCE, PAIC_VIEW_ONLY_MESSAGE } from "constants/peoplePlusAI.constants";
import usePAICStore from "Store/usePAICStore";
import useFullscreenToggle from "Hooks/useFullscreenToggle";
import SbDownloadMarkdownPdfV2 from "Components/HumanEditorV2/SbDownloadMarkdownPdfV2";
import {
  SbPACDocumentHeaderV2,
  SbPACDocumentToolbarV2,
  SbPACDocumentContentV2,
  SbPACDocumentEditPanelV2,
} from "Components/PACDocumentViewV2";

import usePACPeopleJDViewV2 from "./usePACPeopleJDViewV2";
import {
  PageContainer,
  ContentSection,
  DocumentCard,
  EditorRegion,
  HiddenDownloadMount,
} from "../shared/DocumentView.styles";

// PRD §5.15 — tooltip content strings for People JD View
const TOOLTIP_SELECT_JD =
  "Choose which role's People (+AI) JD to view — showing the updated version of the human role after AI agent deployment.";
const TOOLTIP_EDIT = "Edit this People (+AI) JD.";
const TOOLTIP_REVISE =
  "Ask the AI to regenerate this People (+AI) JD with updated inputs.";
const TOOLTIP_PUBLISH_TOGGLE = "Publish or unpublish this People (+AI) JD.";
const TOOLTIP_DOWNLOAD = "Download this People (+AI) JD as a PDF.";

const SbPACPeopleJDViewV2 = () => {
  const hiddenDownloadRef = useRef(null);
  const [downloadRequested, setDownloadRequested] = useState(false);
  const { isFullscreen, handleFullscreenToggle } = useFullscreenToggle();
  const projectSource = usePAICStore((s) => s.projectSource);
  const isReadOnly = projectSource === PAIC_PROJECT_SOURCE.SHARED;

  const {
    loading,
    viewMode,
    jdOptions,
    versionOptions,
    selectedJdId,
    selectedVersion,
    selectedJdData,
    markdownData,
    modifiedText,
    ownerText,
    documentStatus,
    setSelectedJdId,
    handleVersionChange,
    handlePagination,
    handleClose,
    enterEditMode,
    cancelEditMode,
    // handleRevise,        // commented out for now
    handleDownloadTrack,
    // handlePublishToggle, // commented out for now
  } = usePACPeopleJDViewV2();

  const triggerPdfDownload = () => {
    handleDownloadTrack();
    setDownloadRequested(true);
  };

  useEffect(() => {
    if (!downloadRequested) return undefined;

    let frameId;

    const tryClickDownloadLink = () => {
      const link = hiddenDownloadRef.current?.querySelector("a");

      if (link && typeof link.click === "function" && link.href && link.href.startsWith("blob:")) {
        link.click();
        setDownloadRequested(false);
        return;
      }

      frameId = window.requestAnimationFrame(tryClickDownloadLink);
    };

    tryClickDownloadLink();

    return () => {
      if (frameId) {
        window.cancelAnimationFrame(frameId);
      }
    };
  }, [downloadRequested]);

  const getPopupContainer = (node) => node?.parentElement || document.body;

  const TAB_ITEMS = [
    { key: PAIC_TAB_KEYS.JOBS_LIBRARY, label: "Jobs Library", disabled: true },
    { key: PAIC_TAB_KEYS.AGENTS_LIBRARY, label: "Agents Library", disabled: true },
    { key: PAIC_TAB_KEYS.PEOPLE_AI, label: "People + AI" },
    { key: PAIC_TAB_KEYS.SKILLS_BRIDGER, label: "Skills Bridger", disabled: true },
    { key: PAIC_TAB_KEYS.LEARNING_CATALYST, label: "Learning Catalyst", disabled: true },
    { key: PAIC_TAB_KEYS.TALENT_MOBILITY, label: "Talent Mobility", disabled: true },
  ];

  const pageTitle = `Job Details: ${selectedJdData?.job_title ||
    selectedJdData?.paic_jd?.human_jd?.title ||
    "People + AI Job Descriptions"
    }`;

  return (
    <PageContainer $isFullscreen={isFullscreen}>
      {!isFullscreen && (
        <SbPACDocumentHeaderV2
          title={pageTitle}
          viewMode={viewMode}
          activeTab={PAIC_TAB_KEYS.PEOPLE_AI}
          tabItems={TAB_ITEMS}
          showNavigation={true}
          navigationLayout="separate"
          selectedItemId={selectedJdId}
          itemOptions={jdOptions}
          onItemChange={setSelectedJdId}
          onPrev={() => handlePagination("PREV")}
          onNext={() => handlePagination("NEXT")}
          navigationPlaceholder="Select Job Description"
          navigationWidth="280px"
          selectItemTooltip={TOOLTIP_SELECT_JD}
          onClose={handleClose}
          getPopupContainer={getPopupContainer}
        />
      )}

      <ContentSection>
        <DocumentCard>
          <SbPACDocumentToolbarV2
            viewMode={viewMode}
            showVersionDropdown
            versionOptions={versionOptions}
            selectedVersion={selectedVersion}
            onVersionChange={handleVersionChange}
            showVersionMeta={true}
            modifiedText={modifiedText}
            ownerText={ownerText}
            documentStatus={documentStatus}
            showCompareAction={false}
            showDiffAction={false}
            showEditAction={true}
            showReviseAction={false}   /* was true — commented out for now */
            showDownloadAction={true}
            showFullscreenAction={true}
            isFullscreen={isFullscreen}
            onEdit={isReadOnly ? () => SbNotification.info(PAIC_VIEW_ONLY_MESSAGE.title, PAIC_VIEW_ONLY_MESSAGE.description) : enterEditMode}
            editDisabled={isReadOnly}
            /* onRevise={handleRevise} — commented out for now */
            onDownloadPdf={triggerPdfDownload}
            onFullscreenToggle={handleFullscreenToggle}
            editTooltip={isReadOnly ? PAIC_VIEW_ONLY_MESSAGE.description : TOOLTIP_EDIT}
            reviseTooltip={TOOLTIP_REVISE}
            downloadTooltip={TOOLTIP_DOWNLOAD}
            publishTooltip={TOOLTIP_PUBLISH_TOGGLE}
            showPublishAction={false}   /* was true — commented out for now */
          /* onPublishToggle={handlePublishToggle} — commented out for now */
          />

          <EditorRegion>
            {viewMode === PAIC_VIEW_MODES.EDIT ? (
              <SbPACDocumentEditPanelV2
                content={markdownData}
                contentKey={`${selectedJdId}-edit`}
                onCancel={cancelEditMode}
                submitDisabled={true}
              />
            ) : (
              <SbPACDocumentContentV2
                loading={loading}
                content={markdownData}
                contentKey={selectedJdId}
                emptyText="No job description available"
              />
            )}
          </EditorRegion>
        </DocumentCard>
      </ContentSection>

      <HiddenDownloadMount ref={hiddenDownloadRef}>
        {markdownData && (
          <SbDownloadMarkdownPdfV2
            key={`pdf-${selectedJdId}-${selectedVersion}`}
            markdownContent={markdownData}
            jobData={selectedJdData}
          />
        )}
      </HiddenDownloadMount>
    </PageContainer>
  );
};

export default SbPACPeopleJDViewV2;